import { Box, CircularProgress } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  createReviewMutation,
  getReviewQueueOptions,
  listCasesOptions,
  type ReviewQueueGroup,
  type ReviewQueueItem,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  ErrorBoundary,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@platform/design-system";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { usePageTitle } from "../hooks/usePageTitle";
import { RfpLink } from "../shared/RfpLink";

import { DiscrepancyFacts } from "./DiscrepancyFacts";
import { askedForHelp, defaultVerdict } from "./verdicts";
import { type Verdict, VerdictForm } from "./VerdictForm";

import type { ReactNode } from "react";

export const REVIEW_QUEUE_TEST_ID = "golden-review-queue";

const HELP_SECTION = "RUMI asked for help";

interface ReviewSection {
  readonly title: string;
  readonly items: readonly ReviewQueueItem[];
  /** The items RUMI asked a person about, which lead the queue. */
  readonly isHelp: boolean;
}

const evidenceOf = (item: ReviewQueueItem) => item.evidence ?? [];

/**
 * One section per field key, in key order, after one for the fields RUMI asked a
 * person about: each of those needs a call on whether the ask was warranted.
 */
export const toSections = (
  items: readonly ReviewQueueItem[],
): ReviewSection[] => {
  const help = items.filter((item) => askedForHelp(evidenceOf(item)));
  const rest = items.filter((item) => !askedForHelp(evidenceOf(item)));
  const keys = [
    ...new Set(rest.map((item) => item.field_result.field_key)),
  ].sort();
  const byKey = keys.map((key) => ({
    title: key,
    items: rest.filter((item) => item.field_result.field_key === key),
    isHelp: false,
  }));
  return help.length > 0
    ? [{ title: HELP_SECTION, items: help, isHelp: true }, ...byKey]
    : byKey;
};

const ascending = (a: number, b: number): number => a - b;

// The filters' "all" choice. An empty value would leave the field's label sitting on it.
const ALL = "all";

const chosenId = (value: unknown): number | undefined =>
  value === ALL ? undefined : Number(value);

/**
 * The ids a filter offers: those in the queue, plus the active one when the queue no
 * longer holds it (a stale link, or its last verdict just recorded), so the filter
 * still shows what the URL applies.
 */
const withActive = (
  ids: readonly number[],
  active: number | undefined,
): number[] =>
  active === undefined || ids.includes(active)
    ? [...ids]
    : [...ids, active].sort(ascending);

const idParam = (value: string | null): number | undefined =>
  value !== null && /^[1-9][0-9]*$/.test(value) ? Number(value) : undefined;

const ReviewCard = ({
  item,
}: {
  readonly item: ReviewQueueItem;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const [evidenceError, setEvidenceError] = useState<string>();
  const evidence = evidenceOf(item);
  const result = item.field_result;
  // A verdict takes the item off the queue, and can store a correction that moves its
  // case's revision and the run's discrepancies.
  const review = useMutation({
    ...createReviewMutation({ client }),
    onSuccess: () =>
      Promise.all(
        [
          GOLDEN_TAGS.reviews,
          GOLDEN_TAGS.runs,
          GOLDEN_TAGS.corrections,
          GOLDEN_TAGS.cases,
        ].map((tags) => invalidateQuery({ tags: [...tags] })),
      ),
  });

  const record = ({ verdict, reason, correctedValue }: Verdict): void =>
    review.mutate({
      body: {
        field_result_ids: [result.id],
        verdict,
        reason,
        corrected_values:
          verdict === "both_wrong"
            ? { [result.field_path]: correctedValue }
            : undefined,
      },
    });

  return (
    <Card data-testid={`${REVIEW_QUEUE_TEST_ID}-item-${result.id}`}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Link component={RouterLink} to={`/cases/${item.case_id}`}>
            Case #{item.case_id}
          </Link>
          <Link
            component={RouterLink}
            to={`/cases/${item.case_id}/runs/${item.run_id}`}
          >
            Run #{item.run_id}
          </Link>
          <Link component={RouterLink} to={`/cases/${item.case_id}#intake`}>
            Intake data
          </Link>
          <Typography variant="body-medium" component="span">
            Source RFP{" "}
            <RfpLink id={item.source_rfp_id} url={item.source_rfp_url} />
          </Typography>
          <Typography variant="body-medium" component="span">
            RUMI&apos;s RFP{" "}
            <RfpLink id={item.replay_rfp_id} url={item.replay_rfp_url} />
          </Typography>
        </Box>
        <DiscrepancyFacts
          caseId={item.case_id}
          result={result}
          evidence={evidence}
          onEvidenceError={setEvidenceError}
        />
        {evidenceError && <Alert severity="error">{evidenceError}</Alert>}
        <VerdictForm
          result={result}
          evidence={evidence}
          initial={{ verdict: defaultVerdict(evidence), reason: "" }}
          submitLabel="Record verdict"
          pending={review.isPending}
          error={
            review.isError
              ? extractErrorMessage(review.error, "The review failed")
              : undefined
          }
          onSubmit={record}
        />
      </Box>
    </Card>
  );
};

const QueueFilters = ({
  items,
  caseId,
  runId,
  onChange,
}: {
  readonly items: readonly ReviewQueueItem[];
  readonly caseId: number | undefined;
  readonly runId: number | undefined;
  readonly onChange: (next: {
    readonly caseId?: number;
    readonly runId?: number;
  }) => void;
}): ReactNode => {
  const client = useGoldenClient();
  // Labels only decorate the case filter, so the queue works with ids alone if they fail.
  const cases = useQuery(listCasesOptions({ client }));
  const labels = new Map(
    (cases.data ?? []).map((goldenCase) => [goldenCase.id, goldenCase.label]),
  );

  const caseIds = [...new Set(items.map((item) => item.case_id))].sort(
    ascending,
  );
  const inCase = items.filter(
    (item) => caseId === undefined || item.case_id === caseId,
  );
  const runIds = [...new Set(inCase.map((item) => item.run_id))].sort(
    ascending,
  );
  const hasFilter = caseId !== undefined || runId !== undefined;

  return (
    <Box
      sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
    >
      <Select
        data-testid={`${REVIEW_QUEUE_TEST_ID}-case-filter`}
        label="Case"
        value={caseId ?? ALL}
        sx={{ minWidth: 280 }}
        onChange={(event) => onChange({ caseId: chosenId(event.target.value) })}
      >
        <MenuItem value={ALL}>All cases</MenuItem>
        {withActive(caseIds, caseId).map((id) => (
          <MenuItem key={id} value={id}>
            #{id}
            {labels.get(id) ? `: ${labels.get(id)}` : ""}
            {caseIds.includes(id) ? "" : " (no open items)"}
          </MenuItem>
        ))}
      </Select>
      <Select
        data-testid={`${REVIEW_QUEUE_TEST_ID}-run-filter`}
        label="Run"
        value={runId ?? ALL}
        sx={{ minWidth: 160 }}
        onChange={(event) =>
          onChange({ caseId, runId: chosenId(event.target.value) })
        }
      >
        <MenuItem value={ALL}>All runs</MenuItem>
        {withActive(runIds, runId).map((id) => (
          <MenuItem key={id} value={id}>
            #{id}
            {runIds.includes(id) ? "" : " (no open items)"}
          </MenuItem>
        ))}
      </Select>
      {hasFilter && (
        <Button variant="tertiary" onClick={() => onChange({})}>
          Clear
        </Button>
      )}
    </Box>
  );
};

const toItems = (groups: readonly ReviewQueueGroup[]): ReviewQueueItem[] =>
  groups.flatMap((group) => group.items);

/** Every open discrepancy across the cases, with a verdict form on each. */
export const ReviewQueuePage = (): ReactNode => {
  usePageTitle("Review queue");
  const client = useGoldenClient();
  const queue = useQuery(getReviewQueueOptions({ client }));
  // The filters live in the URL, so a filtered queue can be linked to, as the case
  // and run pages do.
  const [params, setParams] = useSearchParams();
  const caseId = idParam(params.get("case"));
  const runId = idParam(params.get("run"));

  const setFilter = (next: {
    readonly caseId?: number;
    readonly runId?: number;
  }): void => {
    const query = new URLSearchParams();
    if (next.caseId !== undefined) query.set("case", String(next.caseId));
    if (next.runId !== undefined) query.set("run", String(next.runId));
    setParams(query);
  };

  if (!queue.isSuccess) {
    return queue.isError ? (
      <Alert data-testid={`${REVIEW_QUEUE_TEST_ID}-error`} severity="error">
        {extractErrorMessage(queue.error, "Failed to load the review queue")}
      </Alert>
    ) : (
      <CircularProgress aria-label="Loading the review queue" />
    );
  }

  const items = toItems(queue.data);
  const shown = items.filter(
    (item) =>
      (caseId === undefined || item.case_id === caseId) &&
      (runId === undefined || item.run_id === runId),
  );

  return (
    <Box
      data-testid={REVIEW_QUEUE_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Typography variant="header-large" component="h1">
        Review queue
      </Typography>
      <Typography
        variant="body-medium"
        color="textSecondary"
        data-testid={`${REVIEW_QUEUE_TEST_ID}-count`}
      >
        {shown.length} open item(s)
        {shown.length !== items.length
          ? ` of ${items.length} in the queue`
          : ""}
        . Decide from the broker documents, the intake data, and the Analyst
        Knowledge Base; RUMI&apos;s reason helps find the evidence but is not
        evidence.
      </Typography>
      <QueueFilters
        items={items}
        caseId={caseId}
        runId={runId}
        onChange={setFilter}
      />
      {toSections(shown).map((section) => (
        <Box
          key={section.title}
          component="section"
          data-testid={`${REVIEW_QUEUE_TEST_ID}-section`}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography
            variant="header-small"
            component="h2"
            sx={section.isHelp ? undefined : { fontFamily: "monospace" }}
          >
            {section.title}
          </Typography>
          {section.isHelp && (
            <Typography variant="body-medium" color="textSecondary">
              RUMI flagged these fields for a person. Decide whether the ask was
              warranted (a valid call-out, which takes the field out of scoring)
              or whether the documents and the Knowledge Base already held the
              answer.
            </Typography>
          )}
          {section.items.map((item) => (
            <ErrorBoundary key={item.field_result.id}>
              <ReviewCard item={item} />
            </ErrorBoundary>
          ))}
        </Box>
      ))}
    </Box>
  );
};
